import { AsyncPipe, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ActivityLogClickDirective } from '@client-ui/directives/activity-log-click.directive';
import { environment } from '@environments/environment';
import { paths } from '@platform/paths';
import { FormFieldWrapperComponent } from '@shared-lib/components/form-components/form-field-wrapper/form-field-wrapper.component';
import { InputTextComponent } from '@shared-lib/components/form-components/input-text/input-text.component';
import { ANALYTIC_CLICK_EVENT_NAMES } from '@shared/constants/analytic-click-event-names';
import { ButtonDirective } from 'primeng/button';
import { take } from 'rxjs/operators';
import { AuthFacadeService } from '../../auth-facade.service';

@Component({
  selector: 'cp-login-email-input',
  templateUrl: './login-email-input.component.html',
  styleUrls: ['../../auth-theme.scss', 'login-email-input.component.scss'],
  imports: [
    AsyncPipe,
    InputTextComponent,
    ReactiveFormsModule,
    FormFieldWrapperComponent,
    ButtonDirective,
    NgOptimizedImage,
    ActivityLogClickDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginEmailInputComponent implements OnInit {
  private readonly facade = inject(AuthFacadeService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  protected readonly isCheckingLoginEmail$ = this.facade.isCheckingLoginEmail$;
  protected readonly email$ = this.facade.email$;
  protected readonly noEmailFoundError$ = this.facade.noEmailFoundError$;

  protected readonly paths = paths;
  protected readonly isMobileApp = environment.mobileApp?.isMobileApp;
  protected readonly AnalyticClickEventNames = ANALYTIC_CLICK_EVENT_NAMES;

  protected loginForm = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
  });

  ngOnInit(): void {
    this.noEmailFoundError$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({ noEmailFound }) => {
      if (noEmailFound) {
        this.loginForm.controls.email.setErrors({ message: 'There is no user account with this e-mail address' });
        this.loginForm.controls.email.markAsTouched();
      }
    });

    this.email$.pipe(take(1)).subscribe((email) => {
      const lastCheckedEmail = localStorage.getItem('LAST_CHECKED_EMAIL') || '';
      this.loginForm.setValue({ email: email || lastCheckedEmail });
    });
  }

  login(): void {
    if (this.loginForm.invalid) {
      return;
    }

    this.facade.checkLoginMail(this.loginForm.value.email);
  }

  createAccount(): void {
    if (!this.isMobileApp) {
      void this.router.navigate([paths.SIGN_UP]);
      return;
    }
    void this.router.navigate([paths.SIGN_UP, paths.signUp.Landlord], {
      queryParams: { 'referral-code': environment.mobileApp?.referralCode },
    });
  }
}
